import type { JSX, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import {
  useAdminPageVisibility,
  type AdminPageHideReason,
} from "@/api/queries/admin/pageVisibility";
import { ProductionMapLink } from "@/components/admin/ProductionMapLink";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";

interface Props {
  pageId: string;
  children: ReactNode;
}

/**
 * Gate an admin route on the tenant's effective page-visibility rules.
 *
 * The page is shown when its ``page_id`` is NOT in the
 * ``hidden_page_ids`` set returned by ``GET /admin/page-visibility``
 * (which folds operation-graph markers AND plan entitlements). Hidden
 * pages render a small 403-style copy whose wording matches the
 * *reason* the page is hidden — graph-edit guidance vs. plan upgrade —
 * so the user doesn't get a "add a node" hint when the real fix is
 * a tier upgrade.
 *
 * Shares the React Query cache with ``AdminLayout``'s usage so this
 * does not double-fetch — it reads the same key.
 */
export function RequirePageVisible({
  pageId,
  children,
}: Props): JSX.Element {
  const { t } = useTranslation("admin");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;
  const vis = useAdminPageVisibility();

  // While loading: render nothing so we don't flash the children only
  // to swap them for a 403 message on the next tick.
  if (vis.isLoading) {
    return <></> as unknown as JSX.Element;
  }

  const hidden = vis.data
    ? new Set(vis.data.hidden_page_ids)
    : new Set<string>();

  if (hidden.has(pageId)) {
    const reason: AdminPageHideReason | undefined =
      vis.data?.hidden_page_reasons?.[pageId];
    return (
      <main className="mx-auto max-w-md px-6 py-20 text-center">
        <p className="text-ink-2">{messageForReason(t, reason)}</p>
        {/* Nedokončený krok má kam odkázat — na rozdíl od plánu a mapy
            výroby je to jedna konkrétní obrazovka. Tentýž odkaz nabízí
            ``MailSetupHint`` uvnitř Pošty; sdílí i jeho klíč, ať se ty
            dvě hlášky nerozejdou. */}
        {/* Funkce mimo tarif: odkaz na tenantův plán, ne vzkaz „zeptejte se
            správce platformy" (KAN-1815) — ten obchodník nemá kam zavolat. */}
        {reason === "entitlements" || reason === "both" ? (
          <Link
            to={`/${lang}/admin/plan`}
            className="mt-4 block font-medium text-ink underline"
            data-testid="page-hidden-plan-link"
          >
            {t("pageHiddenPlanLink", {
              defaultValue: "Zobrazit plán a tarify",
            })}
          </Link>
        ) : null}
        {reason === "not_ready" ? (
          <Link
            to={`/${lang}/admin/embed`}
            className="mt-4 block text-ink-2 underline"
          >
            {t("mail.goToDomainSetup", {
              defaultValue: "Nastavit e-mailovou doménu",
            })}
          </Link>
        ) : null}
        {/* KAN-1356 — the graph reasons say „Otevřete editor v Procesy →
            Tok výroby" and then left the admin to find it. „Zpět do
            adminu" below is not that: it goes back to where they came
            from, not to the screen the sentence is about. */}
        {reason !== "not_ready" && reason !== "entitlements" ? (
          <div className="mt-4">
            <ProductionMapLink className="text-ink-2 underline" />
          </div>
        ) : null}
        <Link
          to={`/${lang}/admin`}
          className="mt-4 inline-block text-ink-3 underline"
        >
          {t("backToAdmin", { defaultValue: "Zpět do adminu" })}
        </Link>
      </main>
    );
  }

  return <>{children}</>;
}

function messageForReason(
  t: ReturnType<typeof useTranslation>["t"],
  reason: AdminPageHideReason | undefined,
): string {
  if (reason === "entitlements") {
    return t("pageHiddenByEntitlement", {
      defaultValue:
        "Tato stránka není dostupná ve vašem plánu. Odemknete ji " +
        "přechodem na vyšší tarif.",
    });
  }
  if (reason === "not_ready") {
    // Vědomě NEmluví o plánu. Tenhle tenant plán má — chybí mu krok
    // v nastavení, a poslat ho upgradovat něco, co je v pořádku, by
    // ho poslalo špatným směrem.
    //
    // ⚠️ Stejně vědomě mluví o **e-mailové** doméně, ne o „vlastní
    // doméně". Jsou to dvě různé věci na dvou různých řádcích v DB:
    // doména obchodu je ``org_branding.custom_domain``, kdežto
    // předpoklad Pošty je ověřená ``OrgEmailProvider.from_email``
    // (``page_visibility._tenant_mail_is_reachable``). Do 2026-09-04
    // tady stálo „firma nemá připojenou vlastní doménu" — tenant
    // s živou doménou obchodu tak četl hlášku, která mu tvrdila
    // nepravdu, a posílala ho opravovat krok, který měl hotový.
    return t("pageHiddenByNotReady", {
      defaultValue:
        "Poštu zatím nabídnout nemůžeme: firma nemá ověřenou " +
        "e-mailovou doménu, takže schránka by neměla adresu, na kterou " +
        "by šlo psát. Doména obchodu na to nestačí — " +
        "e-mailovou doménu ověříte v Kde prodáváte → Odesílat e-maily " +
        "z vlastní domény.",
    });
  }
  if (reason === "both") {
    return t("pageHiddenByBoth", {
      defaultValue:
        "Tato stránka není dostupná — chybí odpovídající uzel v mapě " +
        "výrobních operací A zároveň ji neumožňuje váš plán. Doplňte " +
        "uzel v Procesy → Tok výroby a přejděte na vyšší tarif.",
    });
  }
  // Default + explicit "markers" — preserves the legacy copy so
  // existing tests / muscle memory keep working.
  return t("pageHiddenByGraph", {
    defaultValue:
      "Tato stránka není dostupná, protože v mapě výrobních operací " +
      "chybí odpovídající uzel. Otevřete editor v Procesy → " +
      "Tok výroby a uzel přidejte.",
  });
}
